/**
 * SessionsTableV2 — Sessions list, ported from the prototype's
 * `.sessions` table (cms-redesign-mockup.html ~line 5537+, styling
 * ~838–1244, footer ~929–1003, grouped view ~1006–1081).
 *
 * Two view modes share the same row, column widths, and filters:
 *
 *   List view (default) — server-paginated table.
 *     • Hits GET /sessions?page=&per_page=&… (paginated mode).
 *     • Footer: prev / numbered pager / next + per-page selector.
 *
 *   Grouped view — six accordion sections, one per parent_type.
 *     • Each section fires its own GET /sessions?parent_type=<type>
 *       so the API returns just that bucket. Sections are independent
 *       (own data + loading + error in `state.sessions.grouped[type]`).
 *     • Default open; chevron rotates -90° when collapsed. Section
 *       count badge shows the server total_count for that bucket.
 *     • `has_more` rows are surfaced as a footnote inside the section.
 *
 * Filters and ⌘K search apply to BOTH modes — toggling between them
 * re-runs whatever request the new mode needs with the same query params.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  fetchSessionsPage,
  fetchSessionsGroup,
} from '../../../actions/sessionActions';
import {
  LibTable,
  ListFooter,
  TitleCell,
  ParentChip,
  TierPill,
  AuthorCell,
  DurationCell,
  StatusEdge,
  ChipFilter,
  SegmentedControl,
  IconButton,
  BrandIcon,
  cn,
} from '../../redesign';

/* ─── Field adapters for the paginated API shape ─────────────────────── */

function resolveParent(session) {
  const cat = session.categories?.[0];
  if (cat) return { type: 'category', name: cat.title };
  const sub = session.subcategories?.[0];
  if (sub) {
    return sub.is_book
      ? { type: 'audiobook',   name: sub.title }
      : { type: 'subcategory', name: sub.title };
  }
  const ser = session.series?.[0];
  if (ser) return { type: 'series', name: ser.title };
  const cha = session.challenges?.[0];
  if (cha) return { type: 'challenge', name: cha.title };
  return { type: 'none', name: null };
}

function coverInitials(name) {
  if (!name) return '?';
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function formatRelativeTime(input) {
  if (!input) return '—';
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '—';
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 0) return 'just now';
  const sec = Math.floor(diffMs / 1000);
  const min = Math.floor(sec / 60);
  const hr  = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  if (day === 0) {
    if (hr  >= 1) return `${hr}h ago`;
    if (min >= 1) return `${min}m ago`;
    return 'just now';
  }
  if (day < 7)   return `${day}d ago`;
  if (day < 30)  return `${Math.floor(day / 7)}w ago`;
  if (day < 365) return `${Math.floor(day / 30)}mo ago`;
  return `${Math.floor(day / 365)}y ago`;
}

const EditIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
  </svg>
);

/* ─── CaptionsCell ───────────────────────────────────────────────────────
 *  Renders a compact CC chip in the Captions column based on the row's
 *  `captions` array (shipped by the v2 sessions list endpoint —
 *  SessionRepository::attachCaptionsSummary). Tone follows the worst
 *  status across all languages so the eye lands on rows that need
 *  attention first.
 *
 *    • session_skip_captions ON  → muted "Skipped" pill
 *    • no captions, skip OFF     → "—"
 *    • 1 language (English)      → solid CC chip
 *    • 2+ languages              → CC ×N chip (N = lang count)
 *
 *  Hover title lists the languages + statuses inline so admins can
 *  triage from the list without opening the edit page.
 */
const CAP_TONES = {
  // worst-first ordering when reducing the row's statuses
  failed:              { rank: 0, bg: '#E58A6A26', border: '#E58A6A', color: '#C4715B' },
  auto_pending_review: { rank: 1, bg: '#FCEAD1',   border: '#E8C796', color: '#8C5A1E' },
  pending:             { rank: 1, bg: '#FCEAD1',   border: '#E8C796', color: '#8C5A1E' },
  processing:          { rank: 2, bg: '#DCE7F5',   border: '#B5C6E1', color: '#3A4F7A' },
  queued:              { rank: 3, bg: '#FAF7F1',   border: '#E8E0CF', color: '#2C3E50' },
  human_edited:        { rank: 4, bg: '#D6EBE3',   border: '#A7C7BB', color: '#5C7E72' },
  human_reviewed:      { rank: 4, bg: '#D6EBE3',   border: '#A7C7BB', color: '#5C7E72' },
  edited:              { rank: 4, bg: '#D6EBE3',   border: '#A7C7BB', color: '#5C7E72' },
  reviewed:            { rank: 4, bg: '#D6EBE3',   border: '#A7C7BB', color: '#5C7E72' },
};

const STATUS_LABELS = {
  failed:              'failed',
  auto_pending_review: 'pending',
  pending:             'pending',
  processing:          'processing',
  queued:              'queued',
  human_edited:        'edited',
  human_reviewed:      'reviewed',
  edited:              'edited',
  reviewed:            'reviewed',
};

function worstTone(captions) {
  let worst = null;
  for (const c of captions) {
    const tone = CAP_TONES[(c.status || '').toLowerCase()];
    if (!tone) continue;
    if (worst == null || tone.rank < worst.rank) worst = tone;
  }
  return worst || CAP_TONES.queued;
}

function CaptionsCell({ session }) {
  if (session.session_skip_captions) {
    return (
      <span
        className="font-condensed font-bold uppercase"
        title="Captions pipeline disabled for this session"
        style={{
          fontSize: 10,
          letterSpacing: '0.08em',
          padding: '2px 7px',
          borderRadius: 999,
          background: '#9CA1A81F',
          color: '#9CA1A8',
          whiteSpace: 'nowrap',
        }}
      >
        Skip
      </span>
    );
  }

  const captions = Array.isArray(session.captions) ? session.captions : null;
  if (captions == null) {
    // Endpoint didn't ship the field on this row — older list call or
    // the column was omitted. Render nothing rather than a misleading
    // "no captions" dash.
    return <span className="text-text-light" style={{ fontSize: 12 }}>·</span>;
  }
  if (captions.length === 0) {
    return <span className="text-text-light" style={{ fontSize: 12 }}>—</span>;
  }

  const tone = worstTone(captions);
  const langCount = captions.length;
  const translationCount = captions.filter(
    (c) => (c.language_code || '').toLowerCase() !== 'en',
  ).length;

  // Hover title: "EN reviewed · ES pending · FR processing"
  const tooltip = captions
    .map((c) => `${(c.language_code || '??').toUpperCase()} ${STATUS_LABELS[(c.status || '').toLowerCase()] || c.status || ''}`.trim())
    .join(' · ');

  return (
    <span
      className="inline-flex items-center font-condensed font-bold uppercase whitespace-nowrap"
      title={tooltip}
      style={{
        fontSize: 10.5,
        letterSpacing: '0.06em',
        padding: '2px 8px',
        borderRadius: 999,
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        color: tone.color,
        gap: 5,
      }}
    >
      <BrandIcon name="cc" size={12} />
      {langCount > 1 ? `×${langCount}` : null}
      {translationCount > 0 && langCount === 1 ? null /* shouldn't hit */ : null}
    </span>
  );
}

/* ─── Column widths — applied via <colgroup> so both views and the
 *  thead-less grouped tables get the same alignment. */
const COL_WIDTHS = {
  status:  22,
  session: 'auto',
  parent:  300,
  length:  70,
  tier:    95,
  // New "CC" column — narrow because the chip itself is small. Sits
  // between Tier and Author so the publishing-status cluster
  // (Tier + CC) lives together at glance distance.
  captions: 70,
  author:  160,
  // Bumped from 80 → 100 to give the relative-time string a single
  // comfortable line. At 80, "12mo ago" and similar 8-char values were
  // wrapping under the 14/12 cell padding (only ~56px of content room).
  created: 100,
  actions: 60,
};

function SessionsColgroup() {
  return (
    <colgroup>
      <col style={{ width: COL_WIDTHS.status }} />
      <col />
      <col style={{ width: COL_WIDTHS.parent }} />
      <col style={{ width: COL_WIDTHS.length }} />
      <col style={{ width: COL_WIDTHS.tier }} />
      <col style={{ width: COL_WIDTHS.captions }} />
      <col style={{ width: COL_WIDTHS.author }} />
      <col style={{ width: COL_WIDTHS.created }} />
      <col style={{ width: COL_WIDTHS.actions }} />
    </colgroup>
  );
}

function SessionsHead() {
  return (
    <LibTable.Head>
      <tr>
        <LibTable.Th style={{ paddingLeft: 28 }}>
          <span className="sr-only">Status</span>
        </LibTable.Th>
        <LibTable.Th>Session</LibTable.Th>
        <LibTable.Th>Parent</LibTable.Th>
        <LibTable.Th>Length</LibTable.Th>
        <LibTable.Th>Tier</LibTable.Th>
        <LibTable.Th title="Closed-captioning status">CC</LibTable.Th>
        <LibTable.Th>Author</LibTable.Th>
        <LibTable.Th>Created</LibTable.Th>
        <LibTable.Th> </LibTable.Th>
      </tr>
    </LibTable.Head>
  );
}

/* ─── Single session row ────────────────────────────────────────────────── */
function SessionRow({ session }) {
  const parent = resolveParent(session);
  const badges = [];
  if (session.session_is_recommended) badges.push({ label: 'Rec', tone: 'recommended' });
  if (session.session_is_duplicate)   badges.push({ label: 'Duplicate', tone: 'duplicate' });

  const inactive = !session.session_is_active;
  const author = session.author;

  return (
    <LibTable.Row inactive={inactive}>
      <td
        className="relative border-b border-line group-data-[inactive=true]:bg-[var(--color-row-inactive)] group-data-[inactive=true]:group-hover:bg-[var(--color-row-inactive-hover)]"
        style={{ padding: 0 }}
      >
        <StatusEdge active={!inactive} />
      </td>
      <LibTable.Td>
        <TitleCell
          coverImage={session.session_image}
          coverInitials={coverInitials(session.session_name)}
          coverSeed={session.session_name}
          title={session.session_name}
          badges={badges}
        />
      </LibTable.Td>
      <LibTable.Td>
        <ParentChip type={parent.type} name={parent.name} />
      </LibTable.Td>
      <LibTable.Td>
        <DurationCell seconds={Number(session.session_length_in_sec) || null} />
      </LibTable.Td>
      <LibTable.Td>
        <TierPill isFree={!!session.session_is_free} />
      </LibTable.Td>
      <LibTable.Td>
        <CaptionsCell session={session} />
      </LibTable.Td>
      <LibTable.Td>
        {author?.name ? (
          <AuthorCell name={author.name} imageUrl={author.image} />
        ) : (
          <span className="text-text-light" style={{ fontSize: 12 }}>—</span>
        )}
      </LibTable.Td>
      <LibTable.Td>
        <span className="font-mono text-text-muted" style={{ fontSize: 12.5 }}>
          {formatRelativeTime(session.session_created_at)}
        </span>
      </LibTable.Td>
      <LibTable.Td>
        <div className="inline-flex opacity-0 transition-opacity duration-150 group-hover:opacity-100">
          <Link to={`/sessions/edit/${session.session_id}`} aria-label="Edit session">
            <IconButton size="sm" aria-label="Edit">{EditIconSvg}</IconButton>
          </Link>
        </div>
      </LibTable.Td>
    </LibTable.Row>
  );
}

/* ─── Grouped view: section definitions ──────────────────────────────────
 *  Order intentionally puts the "biggest buckets" first; matches the
 *  prototype's grouped-view accordion order. */
const GROUP_DEFS = [
  { type: 'category',    label: 'Category sessions',    icon: 'categories'  },
  { type: 'subcategory', label: 'Subcategory sessions', icon: 'subcategory' },
  { type: 'audiobook',   label: 'Audiobook sessions',   icon: 'audiobooks'  },
  { type: 'series',      label: 'Series sessions',      icon: 'series'      },
  { type: 'challenge',   label: 'Challenge sessions',   icon: 'challenges'  },
  { type: 'unparented',  label: 'Standalone sessions',  icon: 'close'       },
];

/* ─── Grouped view: one accordion section ─────────────────────────────── */
function GroupSection({ def, group, expanded, onToggle, onLoadMore }) {
  const { data, total_count, has_more, loading, error, per_page } = group;
  // How many more we'll load if the user clicks "Load more" — caps the
  // server's max (200) so a tap can't blow up the page.
  const nextBatch = Math.min(per_page || 50, total_count - data.length);
  return (
    // `.group-section` (prototype 1011–1016): surface bg, sand border,
    // lg radius, overflow hidden, 12px bottom margin.
    <section
      className="bg-surface border border-sand overflow-hidden"
      style={{ borderRadius: 12, marginBottom: 12 }}
    >
      {/* `.group-head` (1018–1031): cream bg, 12/16 padding, hover one notch
       *  darker. Bottom border becomes sand when the section is open. */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className={cn(
          'w-full flex items-center text-left bg-cream cursor-pointer',
          'hover:bg-cream-dark',
        )}
        style={{
          gap: 10,
          padding: '12px 16px',
          borderBottom: expanded ? '1px solid #E8E0D5' : '1px solid transparent',
          transition: 'background 140ms ease, border-color 140ms ease',
        }}
      >
        {/* `.group-chev` (1033–1038) — rotates -90° when collapsed. */}
        <span
          aria-hidden
          className="text-text-muted inline-flex items-center justify-center"
          style={{
            width: 14,
            fontSize: 12,
            transition: 'transform 200ms ease',
            transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
          }}
        >
          ▾
        </span>
        {/* `.group-ico` (1039–1045) — 22×22 teal-d, brand icon inside at 16. */}
        <span
          className="grid place-items-center text-teal-green-dark shrink-0"
          style={{ width: 22, height: 22 }}
        >
          <BrandIcon name={def.icon} size={16} />
        </span>
        {/* `.group-title` (1046–1052) — Asap Condensed 14/700 navy. */}
        <span
          className="font-condensed font-bold text-navy flex-1"
          style={{ fontSize: 14, letterSpacing: '-0.005em' }}
        >
          {def.label}
        </span>
        {/* `.group-count` (1053–1061) — JetBrains Mono 11/600 pill chip. */}
        <span
          className="font-mono text-text-light bg-surface border border-sand inline-flex items-center"
          style={{
            fontSize: 11,
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: 999,
          }}
        >
          {loading && data.length === 0 ? '…' : total_count.toLocaleString()}
        </span>
      </button>

      {/* Body — only mounted when expanded. */}
      {expanded && (
        <div>
          {error ? (
            <div className="text-center text-coral-dark" style={{ padding: '24px 16px', fontSize: 13 }}>
              {error}
            </div>
          ) : loading && data.length === 0 ? (
            <div className="text-center text-text-muted" style={{ padding: '24px 16px', fontSize: 13 }}>
              Loading…
            </div>
          ) : data.length === 0 ? (
            <div className="text-center text-text-muted italic" style={{ padding: '24px 16px', fontSize: 13 }}>
              No {def.label.toLowerCase()} match the current filters.
            </div>
          ) : (
            <LibTable fixed>
              <SessionsColgroup />
              <tbody style={loading ? { opacity: 0.6, pointerEvents: 'none' } : undefined}>
                {data.map((s) => (
                  <SessionRow key={s.session_id} session={s} />
                ))}
              </tbody>
            </LibTable>
          )}
          {has_more && data.length > 0 && (
            // "Load more" — appends the next page to this group's data via
            // the reducer's append-on-page>1 rule. The button stays visible
            // until `has_more` flips to false (i.e. we've drained the bucket).
            <div
              className="flex items-center justify-between bg-cream border-t border-sand"
              style={{ padding: '10px 16px', gap: 12 }}
            >
              <span className="text-text-light" style={{ fontSize: 12.5 }}>
                Showing{' '}
                <strong className="text-navy font-bold">{data.length.toLocaleString()}</strong>{' '}
                of{' '}
                <strong className="text-navy font-bold">{total_count.toLocaleString()}</strong>{' '}
                {def.label.toLowerCase()}
              </span>
              <button
                type="button"
                onClick={onLoadMore}
                disabled={loading}
                className={cn(
                  'inline-flex items-center font-condensed font-bold uppercase',
                  'border border-sand bg-surface rounded-pill',
                  'text-teal-green-dark hover:bg-teal-green-soft hover:border-teal-green',
                  loading && 'opacity-60 cursor-not-allowed',
                )}
                style={{
                  height: 28,
                  padding: '0 14px',
                  fontSize: 11,
                  letterSpacing: '0.06em',
                  gap: 6,
                  transition: 'all 140ms ease',
                }}
              >
                {loading ? 'Loading…' : `Load ${nextBatch.toLocaleString()} more`}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/* ─── Filter chip → API param translation ──────────────────────────────── */
function paramsForFilters({ activeFilter, premiumFilter, recommendedFilter, duplicateFilter }) {
  const out = {};
  if (activeFilter      === 'active')   out.active = '1';
  if (activeFilter      === 'inactive') out.active = '0';
  if (premiumFilter     === 'free')     out.free = '1';
  if (premiumFilter     === 'premium')  out.free = '0';
  if (recommendedFilter === 'yes')      out.recommended = '1';
  if (duplicateFilter   === 'yes')      out.duplicate = '1';
  return out;
}

/* ─── View-toggle icons ─────────────────────────────────────────────────
 *  Lifted from the prototype's `.view-toggle .vt-btn svg` blocks at
 *  line 5580 (list — 3 horizontal lines) and 5584 (grouped — 4 squares). */
const ListIcon = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="4" y1="6" x2="20" y2="6" />
    <line x1="4" y1="12" x2="20" y2="12" />
    <line x1="4" y1="18" x2="20" y2="18" />
  </svg>
);
const GroupedIcon = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
  </svg>
);

/* ─── Main component ─────────────────────────────────────────────────── */
const SessionsTableV2 = ({ searchText = '' }) => {
  const dispatch = useDispatch();
  const paginated  = useSelector((s) => s.sessions.paginated);
  const loading    = useSelector((s) => s.sessions.paginatedLoading);
  const error      = useSelector((s) => s.sessions.paginatedError);
  const groupedAll = useSelector((s) => s.sessions.grouped);

  // UI state
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'grouped'
  const [page,    setPage]    = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [activeFilter,      setActiveFilter]      = useState('all');
  const [recommendedFilter, setRecommendedFilter] = useState('all');
  const [duplicateFilter,   setDuplicateFilter]   = useState('all');
  const [premiumFilter,     setPremiumFilter]     = useState('all');
  // All groups open by default — matches prototype's initial `.is-open` state.
  const [expandedGroups, setExpandedGroups] = useState(() =>
    Object.fromEntries(GROUP_DEFS.map((g) => [g.type, true])),
  );

  // Reset page when any pagination-affecting input changes.
  useEffect(() => {
    setPage(1);
  }, [activeFilter, recommendedFilter, duplicateFilter, premiumFilter, perPage, searchText]);

  const filterParams = useMemo(
    () => paramsForFilters({ activeFilter, premiumFilter, recommendedFilter, duplicateFilter }),
    [activeFilter, premiumFilter, recommendedFilter, duplicateFilter],
  );

  // List view request
  useEffect(() => {
    if (viewMode !== 'list') return;
    dispatch(fetchSessionsPage({
      page,
      per_page: perPage,
      sort: 'created_at',
      dir: 'desc',
      q: searchText || undefined,
      ...filterParams,
    }));
  }, [dispatch, viewMode, page, perPage, searchText, filterParams]);

  // Grouped view — first-page request per parent_type, in parallel. Always
  // starts at page 1 so changing filters/search restarts the bucket. The
  // "Load more" button on each section requests page N+1, and the reducer
  // appends those rows to the section's data.
  useEffect(() => {
    if (viewMode !== 'grouped') return;
    GROUP_DEFS.forEach((g) => {
      dispatch(fetchSessionsGroup(g.type, {
        page: 1,
        per_page: 50,
        sort: 'created_at',
        dir: 'desc',
        q: searchText || undefined,
        ...filterParams,
      }));
    });
  }, [dispatch, viewMode, searchText, filterParams]);

  const toggleGroup = (type) =>
    setExpandedGroups((prev) => ({ ...prev, [type]: !prev[type] }));

  /** Fetch the next page within a single group — appended by the reducer. */
  const loadMoreInGroup = (type) => {
    const cur = groupedAll[type];
    if (!cur || cur.loading || !cur.has_more) return;
    dispatch(fetchSessionsGroup(type, {
      page: (cur.page || 1) + 1,
      per_page: cur.per_page || 50,
      sort: 'created_at',
      dir: 'desc',
      q: searchText || undefined,
      ...filterParams,
    }));
  };

  const rows       = paginated.data;
  const totalCount = paginated.total_count;
  const totalLabel = useMemo(() => totalCount.toLocaleString(), [totalCount]);

  return (
    <div>
      {/* Toolbar — filters + view toggle. */}
      <div
        className="flex items-center flex-wrap"
        style={{ padding: '14px 28px 14px', gap: 8, borderBottom: '1px solid #E8E0D5' }}
      >
        <SegmentedControl
          value={activeFilter}
          onChange={setActiveFilter}
          options={[
            { value: 'all',      label: 'All',      count: activeFilter === 'all'      ? totalLabel : undefined },
            { value: 'active',   label: 'Active',   count: activeFilter === 'active'   ? totalLabel : undefined },
            { value: 'inactive', label: 'Inactive', count: activeFilter === 'inactive' ? totalLabel : undefined },
          ]}
        />
        <ChipFilter
          active={recommendedFilter === 'yes'}
          onClick={() => setRecommendedFilter(recommendedFilter === 'yes' ? 'all' : 'yes')}
          removable
        >Recommended</ChipFilter>
        <ChipFilter
          active={duplicateFilter === 'yes'}
          onClick={() => setDuplicateFilter(duplicateFilter === 'yes' ? 'all' : 'yes')}
          removable
        >Duplicate</ChipFilter>
        <ChipFilter
          active={premiumFilter === 'free'}
          onClick={() => setPremiumFilter(premiumFilter === 'free' ? 'all' : 'free')}
          removable
        >Free</ChipFilter>
        <ChipFilter
          active={premiumFilter === 'premium'}
          onClick={() => setPremiumFilter(premiumFilter === 'premium' ? 'all' : 'premium')}
          removable
        >Premium</ChipFilter>

        {/* `.right-tools` — view-mode toggle. */}
        <div className="ml-auto">
          <SegmentedControl
            value={viewMode}
            onChange={setViewMode}
            shape="md"
            options={[
              { value: 'list',    label: 'List',    icon: ListIcon    },
              { value: 'grouped', label: 'Grouped', icon: GroupedIcon },
            ]}
          />
        </div>
      </div>

      {/* ─── List view ─── */}
      {viewMode === 'list' && (
        <>
          {error && (
            <div className="px-7 py-3 text-coral-dark bg-coral-soft border-b border-coral/40 text-[13px]">
              {error}
            </div>
          )}
          {loading && rows.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-text-muted">
              Loading sessions…
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center text-text-muted py-16">
              No sessions match the current filters.
            </div>
          ) : (
            <>
              <LibTable fixed>
                <SessionsColgroup />
                <SessionsHead />
                <tbody style={loading ? { opacity: 0.6, pointerEvents: 'none' } : undefined}>
                  {rows.map((s) => (
                    <SessionRow key={s.session_id} session={s} />
                  ))}
                </tbody>
              </LibTable>
              <ListFooter
                page={paginated.page}
                perPage={paginated.per_page}
                totalCount={paginated.total_count}
                pageCount={paginated.page_count}
                onPageChange={setPage}
                onPerPageChange={setPerPage}
                itemLabel="sessions"
              />
            </>
          )}
        </>
      )}

      {/* ─── Grouped view ─── */}
      {viewMode === 'grouped' && (
        // `.sessions-grouped-view` (prototype 1006–1009): bg, 16/28 padding.
        <div style={{ padding: '16px 28px 32px', background: '#FFFCF5' }}>
          {GROUP_DEFS.map((def) => (
            <GroupSection
              key={def.type}
              def={def}
              group={groupedAll[def.type]}
              expanded={!!expandedGroups[def.type]}
              onToggle={() => toggleGroup(def.type)}
              onLoadMore={() => loadMoreInGroup(def.type)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SessionsTableV2;
